import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  CheckCircle2,
  Target,
  TrendingUp,
  Users,
  Zap,
  BarChart3,
  BookOpen,
  MessageCircle,
  Phone,
  Award,
  Clock3,
  Layers,
  Wrench,
  Sparkles,
  AlertCircle,
} from "lucide-react";
import { Header, Footer } from "../components/Shell";
import { api } from "../lib/api";
import { useSeo, jsonLdService, jsonLdFaq } from "../lib/seo";
import { useSiteContent, rich } from "../lib/siteContent";

const WA_ADMIN = "https://wa.me/6285157009099?text=" + encodeURIComponent(
  "Hallo Admin Rara, saya ingin mendapatkan informasi mengenai layanan Mas Tofa."
);

/* ---------- Reusable inline lead form ---------- */
function LeadForm({ service, defaultBusiness = "", cta = "Kirim & Konsultasi" }) {
  const location = useLocation();
  const [form, setForm] = useState({
    name: "",
    whatsapp: "",
    email: "",
    business: defaultBusiness,
    product: "",
    marketplace: "",
    price_range: "",
    monthly_revenue: "",
    revenue_target: "",
    ad_platform: "",
    ad_budget: "",
    main_problem: "",
    message: "",
  });
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const utm = Object.fromEntries(new URLSearchParams(location.search));
      await api("/leads", {
        method: "POST",
        data: {
          ...form,
          service,
          utm_source: utm.utm_source || "",
          utm_medium: utm.utm_medium || "",
          utm_campaign: utm.utm_campaign || "",
          utm_content: utm.utm_content || "",
          utm_term: utm.utm_term || "",
          referrer: document.referrer || "",
          page: location.pathname,
        },
      });
      setSent(true);
      window.mastofaTrack?.("Lead", { content_name: service });
      window.mastofaTrack?.("service_inquiry", { service });
    } catch (err) {
      const detail = err.response?.data?.detail;
      let msg = "Gagal mengirim. Coba lagi.";
      if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail) && detail[0]?.msg)
        msg = detail.map((d) => d.msg).join(", ");
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="lead-sent" data-testid={`lead-sent-${service}`}>
        <CheckCircle2 size={40} />
        <b>Terima kasih!</b>
        <p>
          Data Anda sudah kami terima. Admin Rara akan menghubungi Anda melalui
          WhatsApp secepatnya di jam kerja (09.00–20.00 WIB).
        </p>
        <a href={WA_ADMIN} target="_blank" rel="noreferrer" className="button">
          Chat WhatsApp sekarang <ArrowRight size={16} />
        </a>
      </div>
    );
  }

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <form onSubmit={submit} className="lead-form" data-testid={`lead-form-${service}`}>
      <div className="lead-grid">
        <label>
          Nama lengkap*
          <input required value={form.name} onChange={set("name")}
            data-testid="lead-name" />
        </label>
        <label>
          WhatsApp*
          <input required type="tel" inputMode="numeric"
            placeholder="628123456789"
            pattern="[0-9]{9,15}"
            value={form.whatsapp}
            onChange={(e) => setForm({ ...form, whatsapp: e.target.value.replace(/\D/g, "") })}
            data-testid="lead-whatsapp" />
        </label>
        <label>
          Email (opsional)
          <input type="email" value={form.email} onChange={set("email")}
            data-testid="lead-email" />
        </label>
        <label>
          Nama bisnis / brand
          <input value={form.business} onChange={set("business")}
            data-testid="lead-business" />
        </label>
        <label>
          Produk yang dijual
          <input placeholder="misal: baju gamis wanita"
            value={form.product} onChange={set("product")}
            data-testid="lead-product" />
        </label>
        <label>
          Platform jualan
          <select value={form.marketplace} onChange={set("marketplace")}
            data-testid="lead-marketplace">
            <option value="">Pilih…</option>
            <option>Shopee</option>
            <option>TikTok Shop</option>
            <option>Tokopedia</option>
            <option>Website / Landing Page</option>
            <option>WhatsApp / DM</option>
            <option>Lainnya</option>
          </select>
        </label>
        <label>
          Harga jual per pcs
          <input placeholder="Rp 100.000" value={form.price_range}
            onChange={set("price_range")} data-testid="lead-price" />
        </label>
        <label>
          Omzet saat ini per bulan
          <input placeholder="Rp 10jt" value={form.monthly_revenue}
            onChange={set("monthly_revenue")} data-testid="lead-revenue" />
        </label>
        <label>
          Target omzet per bulan
          <input placeholder="Rp 50jt" value={form.revenue_target}
            onChange={set("revenue_target")} data-testid="lead-target" />
        </label>
        <label>
          Sudah pernah iklan? Platform apa?
          <input placeholder="Meta Ads / TikTok Ads / belum"
            value={form.ad_platform} onChange={set("ad_platform")}
            data-testid="lead-ad-platform" />
        </label>
        <label>
          Budget iklan per bulan
          <input placeholder="Rp 10jt" value={form.ad_budget}
            onChange={set("ad_budget")} data-testid="lead-ad-budget" />
        </label>
        <label style={{ gridColumn: "1 / -1" }}>
          Masalah utama sekarang
          <textarea rows="3" value={form.main_problem}
            placeholder="misal: order stagnan, biaya iklan mahal, tidak tahu strategi…"
            onChange={set("main_problem")} data-testid="lead-problem" />
        </label>
      </div>
      {error && <div className="error" data-testid="lead-error">{error}</div>}
      <div className="lead-actions">
        <button className="button" disabled={busy} data-testid="lead-submit">
          {busy ? "Mengirim…" : cta} <ArrowRight size={16} />
        </button>
        <span className="lead-note">
          <ShieldCheck size={13} /> Data Anda hanya digunakan untuk keperluan konsultasi ini.
        </span>
      </div>
    </form>
  );
}

/* ---------- JASA IKLAN ---------- */
export function JasaIklan() {
  const { t: c, list } = useSiteContent("jasa_iklan");
  useEffect(() => {
    window.mastofaTrack?.("service_ads_view");
  }, []);
  useSeo({
    title: "Jasa Iklan Meta, TikTok, Shopee — Kelola & Pendampingan",
    description:
      "Jasa iklan Meta Ads, TikTok Ads, Shopee Ads dari MASTOFA.COM. Paket 1 bulan Rp 5.000.000 atau 3 bulan Rp 10.500.000. Termasuk evaluasi bisnis, training tim, dan laporan bulanan.",
    path: "/jasa-iklan",
    jsonLd: jsonLdService({
      name: "Jasa Iklan Digital (Meta, TikTok, Shopee)",
      description:
        "Jasa pengelolaan iklan Meta Ads, TikTok Ads, dan Shopee Ads dengan evaluasi bisnis, pendampingan, training tim, dan laporan bulanan. Komitmen minimal 1 tahun.",
      price: 5000000,
      path: "/jasa-iklan",
    }),
    jsonLdId: "seo-jsonld-jasa-iklan",
  });

  return (
    <div>
      <Header />
      <main className="service-page">
        <section className="svc-hero">
          <div>
            <span className="section-kicker">
              <Target size={13} style={{ display: "inline", marginRight: 6 }} />
              {c("hero.kicker")}
            </span>
            <h1>{rich(c("hero.title"), "em")}</h1>
            <p>{c("hero.body")}</p>
            <div className="svc-hero-cta">
              <a href="#lead" className="button" data-testid="svc-ads-cta-primary">
                {c("hero.cta1")} <ArrowRight size={16} />
              </a>
              <a href={WA_ADMIN} target="_blank" rel="noreferrer"
                 className="button-ghost" data-testid="svc-ads-cta-wa">
                <MessageCircle size={16} /> {c("hero.cta2")}
              </a>
            </div>
            <div className="trust-row" style={{ marginTop: 24 }}>
              <ShieldCheck size={16} /> {c("hero.trust1")}
              <span /> <CheckCircle2 size={16} /> {c("hero.trust2")}
              <span /> <Award size={16} /> {c("hero.trust3")}
            </div>
          </div>
          <div className="svc-hero-price">
            <span>{c("pricing.label")}</span>
            <div className="svc-price-box">
              <b>{c("pricing.p1_title")}</b>
              <strong>{c("pricing.p1_price")}</strong>
              <small>{c("pricing.p1_sub")}</small>
            </div>
            <div className="svc-price-box featured">
              <span className="badge-save">{c("pricing.p2_badge")}</span>
              <b>{c("pricing.p2_title")}</b>
              <strong>{c("pricing.p2_price")}</strong>
              <small>{c("pricing.p2_sub")}</small>
            </div>
            <div className="svc-contract">
              <AlertCircle size={14} />
              <span>{rich(c("pricing.contract"), "b")}</span>
            </div>
          </div>
        </section>

        <section className="svc-band svc-includes">
          <h2>{rich(c("includes.title"), "em")}</h2>
          <div className="svc-grid-4">
            {list("includes.items").map((x0, i) => {
              const icons = [<BarChart3 size={22} />, <Target size={22} />, <Zap size={22} />, <TrendingUp size={22} />, <BookOpen size={22} />, <Users size={22} />, <MessageCircle size={22} />, <Layers size={22} />];
              const x = { ...x0, i: icons[i % icons.length] };
              return (
              <div key={i} className="value-card">
                <div className="value-icon">{x.i}</div>
                <b>{x.t}</b>
                <p>{x.d}</p>
              </div>
              );
            })}
          </div>
        </section>

        <section className="svc-band">
          <div className="svc-flow">
            <div>
              <span className="section-kicker">{c("flow.kicker")}</span>
              <h2>{c("flow.title")}</h2>
              <p>{c("flow.body")}</p>
            </div>
            <ol className="svc-flow-list">
              {list("flow.steps").map((s, i) => (<li key={i}><b>{s.title}</b><span>{s.body}</span></li>))}
            </ol>
          </div>
        </section>

        <section className="svc-band svc-transparent">
          <h2>{rich(c("notes.title"), "em")}</h2>
          <div className="svc-notes">
            {list("notes.items").map((n, i) => (
              <div key={i}>
                <b>{n.title}</b>
                <p>{n.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section id="lead" className="svc-band svc-lead">
          <div className="svc-lead-head">
            <span className="section-kicker">{c("lead.kicker")}</span>
            <h2>{c("lead.title")}</h2>
            <p>{c("lead.body")}</p>
          </div>
          <LeadForm service="jasa_iklan" cta="Kirim & Konsultasi" />
        </section>
      </main>
      <Footer />
    </div>
  );
}

/* ---------- JASA LANDING PAGE ---------- */
export function JasaLandingPage() {
  useEffect(() => {
    window.mastofaTrack?.("landing_page_service_view");
  }, []);
  useSeo({
    title: "Jasa Pembuatan Landing Page — Meta & TikTok Ads",
    description:
      "Jasa pembuatan landing page mobile-first untuk campaign Meta Ads, TikTok Ads, dan Shopee Ads. Copywriting PAS, tracking Pixel siap, SEO dasar terpasang.",
    path: "/jasa-landing-page",
    jsonLd: jsonLdService({
      name: "Jasa Pembuatan Landing Page",
      description:
        "Halaman penjualan yang dirancang khusus untuk campaign Meta Ads, TikTok Ads, dan Shopee Ads. Mobile-first, copywriting terarah, dan siap tracking.",
      path: "/jasa-landing-page",
    }),
    jsonLdId: "seo-jsonld-jasa-lp",
  });

  return (
    <div>
      <Header />
      <main className="service-page">
        <section className="svc-hero">
          <div>
            <span className="section-kicker">
              <Layers size={13} style={{ display: "inline", marginRight: 6 }} />
              JASA PEMBUATAN LANDING PAGE
            </span>
            <h1>
              Landing page yang <em>fokus</em>,<br />
              dibuat untuk <em>closing</em>.
            </h1>
            <p>
              Halaman penjualan yang dirancang khusus untuk campaign Meta Ads,
              TikTok Ads, dan Shopee Ads. Struktur konten copywriting, section
              conversion, mobile-first, dan siap tracking.
            </p>
            <div className="svc-hero-cta">
              <a href="#lead" className="button" data-testid="svc-lp-cta-primary">
                Konsultasi Landing Page <ArrowRight size={16} />
              </a>
              <a href={WA_ADMIN} target="_blank" rel="noreferrer"
                 className="button-ghost" data-testid="svc-lp-cta-wa">
                <MessageCircle size={16} /> Tanya via WhatsApp
              </a>
            </div>
          </div>
          <div className="svc-hero-visual">
            <img
              src="https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?auto=format&fit=crop&w=1200&q=80"
              alt="Landing page mockup" />
          </div>
        </section>

        <section className="svc-band">
          <h2>Cocok untuk</h2>
          <div className="svc-grid-4">
            {[
              { t: "Campaign Meta Ads", d: "Halaman khusus dengan Pixel + CAPI aktif." },
              { t: "Campaign TikTok Ads", d: "LP mobile-first + TikTok Pixel & Events API." },
              { t: "Campaign Shopee Ads", d: "Bridging page dengan CTA ke marketplace." },
              { t: "Konversi WhatsApp", d: "Form + tombol WA dengan tracking Lead." },
              { t: "Peluncuran produk", d: "Halaman pre-order / promo terbatas." },
              { t: "Kampanye kolaborasi", d: "Halaman khusus untuk affiliate / KOL." },
            ].map((x) => (
              <div key={x.t} className="value-card">
                <b>{x.t}</b>
                <p>{x.d}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="svc-band">
          <div className="svc-flow">
            <div>
              <span className="section-kicker">FITUR STANDAR</span>
              <h2>Yang selalu Anda dapatkan</h2>
              <p>Semua landing page kami dibuat dengan standar berikut.</p>
            </div>
            <ol className="svc-flow-list">
              <li><b>Copywriting terarah</b><span>Struktur PAS (Problem, Agitate, Solution) yang jelas.</span></li>
              <li><b>Mobile-first</b><span>Loading cepat, layout enak dibaca di HP.</span></li>
              <li><b>Tracking siap</b><span>Meta Pixel, TikTok Pixel, GA4 — event conversion terpasang.</span></li>
              <li><b>Form / CTA</b><span>Form lead atau tombol WA dengan pesan pre-filled.</span></li>
              <li><b>SEO dasar</b><span>Meta title, description, Open Graph.</span></li>
              <li><b>Revisi</b><span>Detail revisi & garansi dikonfirmasi saat onboarding.</span></li>
            </ol>
          </div>
        </section>

        <section className="svc-band">
          <div className="svc-inline-info">
            <Wrench size={22} />
            <div>
              <b>Harga & durasi</b>
              <p>
                Harga dan durasi pembuatan tergantung pada scope (jumlah section,
                tingkat kompleksitas, jumlah halaman). Kami sampaikan penawaran
                final setelah memahami kebutuhan Anda pada sesi konsultasi.
              </p>
            </div>
          </div>
        </section>

        <section id="lead" className="svc-band svc-lead">
          <div className="svc-lead-head">
            <span className="section-kicker">KONSULTASI LANDING PAGE</span>
            <h2>Ceritakan kebutuhan Anda</h2>
            <p>Isi form berikut. Kami akan menghubungi Anda untuk penawaran final.</p>
          </div>
          <LeadForm service="landing_page" cta="Konsultasikan Kebutuhan LP" />
        </section>
      </main>
      <Footer />
    </div>
  );
}

/* ---------- COMPLAINT / PENGADUAN ---------- */
export function Complaint() {
  const [form, setForm] = useState({
    name: "",
    contact: "",
    order_id: "",
    subject: "",
    description: "",
  });
  const [ticket, setTicket] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useSeo({
    title: "Pengaduan & Bantuan Resmi",
    description:
      "Kirim pengaduan resmi ke MASTOFA.COM. Setiap tiket mendapat nomor unik untuk pelacakan.",
    path: "/complaint",
  });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const r = await api("/complaints", { method: "POST", data: form });
      setTicket(r.ticket);
    } catch (err) {
      const detail = err.response?.data?.detail;
      let msg = "Gagal mengirim. Coba lagi.";
      if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail) && detail[0]?.msg)
        msg = detail.map((d) => d.msg).join(", ");
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Header />
      <main className="page">
        <div className="page-intro">
          <span className="section-kicker">PENGADUAN & BANTUAN</span>
          <h1>Ada yang bisa <em>kami bantu?</em></h1>
          <p>
            Kirim pengaduan atau permintaan bantuan resmi. Anda akan mendapatkan
            nomor tiket untuk memudahkan pelacakan.
          </p>
        </div>

        <div className="complaint-wrap">
          <div className="complaint-side">
            <b>Kontak cepat</b>
            <p style={{ marginTop: 4 }}>
              WhatsApp Admin Rara<br />
              <a href={WA_ADMIN} target="_blank" rel="noreferrer" className="text-link">
                0851-5700-9099 <ArrowRight size={14} />
              </a>
            </p>
            <div style={{ height: 20 }} />
            <b>Jam operasional</b>
            <p style={{ marginTop: 4 }}>Senin – Sabtu, 09.00 – 20.00 WIB</p>
            <div style={{ height: 20 }} />
            <b>Alamat</b>
            <p style={{ marginTop: 4 }}>
              Jl. Soekarno Hatta No. 381<br />
              Bugangin, Kec. Kendal<br />
              Kab. Kendal, Jawa Tengah 51314
            </p>
          </div>

          <div className="complaint-form">
            {ticket ? (
              <div className="lead-sent" data-testid="complaint-sent">
                <CheckCircle2 size={40} />
                <b>Pengaduan diterima</b>
                <p>
                  Nomor tiket Anda: <b style={{ color: "var(--blue)" }}>{ticket}</b>.
                  Simpan nomor ini untuk keperluan follow-up.
                </p>
                <a href={WA_ADMIN} target="_blank" rel="noreferrer" className="button">
                  Info lanjutan via WhatsApp <ArrowRight size={16} />
                </a>
              </div>
            ) : (
              <form onSubmit={submit} className="lead-form" data-testid="complaint-form">
                <div className="lead-grid">
                  <label>
                    Nama*
                    <input required value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      data-testid="complaint-name" />
                  </label>
                  <label>
                    Email / WhatsApp*
                    <input required value={form.contact}
                      onChange={(e) => setForm({ ...form, contact: e.target.value })}
                      data-testid="complaint-contact" />
                  </label>
                  <label style={{ gridColumn: "1 / -1" }}>
                    Order ID (jika ada)
                    <input value={form.order_id}
                      onChange={(e) => setForm({ ...form, order_id: e.target.value })}
                      data-testid="complaint-order" />
                  </label>
                  <label style={{ gridColumn: "1 / -1" }}>
                    Subjek*
                    <input required placeholder="Contoh: Bukti bayar sudah masuk tapi belum aktif"
                      value={form.subject}
                      onChange={(e) => setForm({ ...form, subject: e.target.value })}
                      data-testid="complaint-subject" />
                  </label>
                  <label style={{ gridColumn: "1 / -1" }}>
                    Deskripsi masalah*
                    <textarea required rows="5" value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      data-testid="complaint-description" />
                  </label>
                </div>
                {error && <div className="error">{error}</div>}
                <div className="lead-actions">
                  <button className="button" disabled={busy} data-testid="complaint-submit">
                    {busy ? "Mengirim…" : "Kirim Pengaduan"} <ArrowRight size={16} />
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

/* ---------- GENERIC MARKETING PAGES (About / FAQ / Contact / Legal) ---------- */

const CONTACT_INFO = {
  address_line: "Jl. Soekarno Hatta No. 381, Bugangin",
  address_2: "Kec. Kendal, Kab. Kendal, Jawa Tengah 51314",
  whatsapp: "0851-5700-9099",
  wa_owner: "Rara (Admin)",
  wa_link: "https://wa.me/6285157009099",
};

export function About() {
  const { t: c, list } = useSiteContent("about");
  useSeo({
    title: "Tentang Mas Tofa",
    description:
      "Tentang MASTOFA.COM — Digital Marketer fokus Meta Ads, TikTok Ads, Shopee Ads, landing page, dan pendampingan bisnis.",
    path: "/about",
  });
  return (
    <div>
      <Header />
      <main className="page">
        <div className="page-intro">
          <span className="section-kicker">{c("intro.kicker")}</span>
          <h1>{rich(c("intro.title"), "em")}</h1>
          <p>{c("intro.body")}</p>
        </div>

        <div className="about-grid">
          <div>
            <h3>{c("focus.title")}</h3>
            <p>{c("focus.body")}</p>
            <ul className="about-list">
              {list("focus.items").map((x, i) => (<li key={i}><CheckCircle2 size={15} /> {x.text}</li>))}
            </ul>
          </div>
          <div>
            <h3>{c("approach.title")}</h3>
            <p>{c("approach.body1")}</p>
            <p>{c("approach.body2")}</p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export function FAQPage() {
  const [settings, setSettings] = useState({});
  const { t: c, raw } = useSiteContent("faq");
  useEffect(() => {
    api("/settings/public").then(setSettings).catch(() => {});
  }, []);
  const DEFAULT_FAQ = [
    { q: "Apakah materi kursus bisa diakses selamanya?", a: "Ya, kursus digital yang Anda beli bisa diakses selamanya selama platform beroperasi." },
    { q: "Apakah harga Jasa Iklan bisa dibayar bulanan?", a: "Paket pembayaran tersedia untuk 1 bulan (Rp 5.000.000) atau 3 bulan (Rp 10.500.000 – setara Rp 3.500.000/bulan). Komitmen layanan minimal 1 tahun." },
    { q: "Apakah Jasa Iklan menjamin omzet atau profit?", a: "Tidak. Digital marketing tidak menjamin omzet atau profit pasti. Kami menjamin proses, pengelolaan, edukasi, dan pendampingan yang profesional." },
    { q: "Bagaimana metode pembayarannya?", a: "Semua pembayaran diproses aman melalui Duitku (QRIS, VA, e-wallet, retail)." },
    { q: "Apakah data saya aman?", a: "Ya. Password disimpan dengan bcrypt, koneksi HTTPS, dan data hanya dibagikan ke penyedia layanan operasional yang tercantum di Kebijakan Privasi." },
    { q: "Bagaimana kalau ada kendala setelah pembayaran?", a: "Hubungi WhatsApp Admin Rara di 0851-5700-9099 atau kirim pengaduan resmi via halaman Pengaduan." },
  ];
  const remote = Array.isArray(settings.faq) ? settings.faq : [];
  // If admin config has fewer than 6, top up with defaults (unique by question).
  const seen = new Set(remote.map((f) => (f.q || "").trim().toLowerCase()));
  const merged = [...remote];
  for (const f of DEFAULT_FAQ) {
    if (merged.length >= 6) break;
    const key = f.q.trim().toLowerCase();
    if (!seen.has(key)) {
      merged.push(f);
      seen.add(key);
    }
  }
  const editorFaq = raw("list.items");
  const list = Array.isArray(editorFaq) && editorFaq.length ? editorFaq.filter((f) => f.q) : merged;

  useSeo({
    title: "FAQ — Pertanyaan yang Sering Diajukan",
    description:
      "Pertanyaan seputar Jasa Iklan, Landing Page, Privat 1-on-1, VIP Member, dan kursus MASTOFA.COM.",
    path: "/faq",
    jsonLd: jsonLdFaq(list),
    jsonLdId: "seo-jsonld-faq",
  });
  return (
    <div>
      <Header />
      <main className="page">
        <div className="page-intro">
          <span className="section-kicker">{c("intro.kicker")}</span>
          <h1>{rich(c("intro.title"), "em")}</h1>
        </div>
        <div className="faq-list">
          {list.map((f, i) => (
            <details key={i} className="faq-item" data-testid={`faq-item-${i}`}>
              <summary>{f.q}</summary>
              <p>{f.a}</p>
            </details>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}

export function Contact() {
  const { t: c } = useSiteContent("contact");
  useSeo({
    title: "Kontak & WhatsApp Admin",
    description:
      "Hubungi MASTOFA.COM untuk pertanyaan layanan, kerja sama, atau bantuan teknis. WhatsApp Admin Rara 0851-5700-9099.",
    path: "/contact",
  });
  return (
    <div>
      <Header />
      <main className="page">
        <div className="page-intro">
          <span className="section-kicker">{c("intro.kicker")}</span>
          <h1>{rich(c("intro.title"), "em")}</h1>
          <p>{c("intro.body")}</p>
        </div>

        <div className="contact-grid">
          <a href={c("wa.link")} target="_blank" rel="noreferrer"
             className="contact-card" data-testid="contact-wa">
            <MessageCircle size={22} />
            <b>{c("wa.title")}</b>
            <span>{c("wa.number")} — {c("wa.owner")}</span>
            <small>{c("wa.hours")}</small>
          </a>
          <div className="contact-card">
            <Phone size={22} />
            <b>{c("address.title")}</b>
            <span>{c("address.line1")}</span>
            <span>{c("address.line2")}</span>
          </div>
          <Link to="/complaint" className="contact-card" data-testid="contact-complaint">
            <AlertCircle size={22} />
            <b>{c("complaint.title")}</b>
            <span>{c("complaint.body")}</span>
            <small>{c("complaint.small")}</small>
          </Link>
          <Link to="/jasa-iklan" className="contact-card" data-testid="contact-jasa-iklan">
            <Sparkles size={22} />
            <b>{c("jasa.title")}</b>
            <span>{c("jasa.body")}</span>
            <small>{c("jasa.small")}</small>
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
